import { AgmCoreModule } from '@agm/core';
import { HttpClientXsrfModule } from '@angular/common/http';
import {
  ErrorHandler,
  NgModule,
  NgModuleFactoryLoader,
  PLATFORM_ID,
  SystemJsNgModuleLoader,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  BrowserModule,
  BrowserTransferStateModule,
} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  ABTest,
  ABTestOverrides,
  ABTEST_WHITELIST_COOKIE,
} from '@freelancer/abtest';
import { ABTestTesting } from '@freelancer/abtest/testing';
import { Auth, AUTH_CONFIG } from '@freelancer/auth';
import { CAPTCHA_CONFIG } from '@freelancer/captcha';
import {
  APPS_DOMAINS_MAP,
  APP_NAME,
  ENVIRONMENT_NAME,
  SITE_NAME,
} from '@freelancer/config';
import {
  Datastore,
  datastoreActionSanitizer,
  DatastoreFake,
  DatastoreModule,
  DatastoreModuleFake,
} from '@freelancer/datastore';
import { FacebookModule } from '@freelancer/facebook';
import { FileUploadLegacyModule } from '@freelancer/file-upload-legacy';
import {
  FreelancerHttp,
  FREELANCER_HTTP_ABTEST_OVERRIDES_PROVIDER,
  FREELANCER_HTTP_AUTH_PROVIDERS,
  FREELANCER_HTTP_CONFIG,
} from '@freelancer/freelancer-http';
import { SITE_WIDE_LINK_TAGS } from '@freelancer/link-tags';
import { LoginSignupModule } from '@freelancer/login-signup';
import { ThreatmetrixModule } from '@freelancer/threatmetrix';
import { TimeUtilsModule } from '@freelancer/time-utils';
import {
  CustomErrorHandler,
  ErrorTracking,
  FacebookPixelTracking,
  GoogleTracking,
  PerformanceTracking,
  Tracking,
  TrackingModule,
} from '@freelancer/tracking';
import {
  ErrorTrackingTesting,
  FacebookPixelTrackingTesting,
  GoogleTrackingTesting,
  PerformanceTrackingTesting,
  TrackingTesting,
} from '@freelancer/tracking/testing';
import { UiModule } from '@freelancer/ui';
import { ModalModule } from '@freelancer/ui/modal';
import { UserAgentModule } from '@freelancer/user-agent';
import { CookieModule, COOKIE_OPTIONS } from '@laurentgoudet/ngx-cookie';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ActionSanitizer } from '@ngrx/store-devtools/src/config';
import { environment } from '../environments/environment';
import { AppModalsModule } from './app-modals.module';
import { cookieOptionsFactory } from './cookie-options.factory';

/* The type for ActionSanitizer doesn't allow a custom action type.
 * ActionSanitizer probably should be
 *  export declare type ActionSanitizer<A extends Action = Action> = (
 *    action: A,
 *    id: number,
 *  ) => A;
 * but it isn't so let's cast through `any` for now.
 */
const actionSanitizer: ActionSanitizer = datastoreActionSanitizer as any;

// /!\ DO ADD ANYTHING IN THERE WITHOUT TALKING TO FRONTEND INFRA FIRST /!\
// Application-wide code should be avoided as much as possible; does your code
// needs:
// - to be loaded on ALL logged-in pages?
// - AND to be loaded on ALL logged-out pages?
// - AND to be loaded on the Admin?
// - ...
// Probably not.
@NgModule({
  imports: [
    BrowserModule.withServerTransition({ appId: 'webapp' }),
    BrowserTransferStateModule,
    BrowserAnimationsModule,
    FormsModule,
    HttpClientXsrfModule.disable(), // no part of the app should rely on that
    AppModalsModule,
    AgmCoreModule.forRoot(environment.mapsConfig),
    UiModule.initialize(environment.uiConfig),
    ModalModule,
    CookieModule.forRoot(),
    ThreatmetrixModule.initialize(environment.threatmetrixConfig),
    DatastoreModule.initialize({
      webSocketUrl: environment.datastoreConfig.webSocketUrl,
      enableStoreFreeze: environment.datastoreConfig.enableStoreFreeze,
      httpAdapter: FreelancerHttp,
      requestData: {},
    }),
    environment.useFakes
      ? DatastoreModuleFake.initialize({
          initializeFake: true,
        })
      : [],
    !environment.production
      ? StoreDevtoolsModule.instrument({
          maxAge: 150,
          actionSanitizer,
        })
      : [],
    TrackingModule.initialize(environment.trackingConfig),
    TimeUtilsModule,
    FileUploadLegacyModule,
    FacebookModule.initialize(environment.facebookConfig),
    UserAgentModule,
    LoginSignupModule,
  ],
  providers: [
    { provide: AUTH_CONFIG, useValue: environment.authConfig },
    environment.useFakes
      ? [
          { provide: ABTest, useClass: ABTestTesting },
          { provide: Datastore, useClass: DatastoreFake },
          { provide: Tracking, useClass: TrackingTesting },
          { provide: ErrorTracking, useClass: ErrorTrackingTesting },
          {
            provide: PerformanceTracking,
            useClass: PerformanceTrackingTesting,
          },
          { provide: GoogleTracking, useClass: GoogleTrackingTesting },
          {
            provide: FacebookPixelTracking,
            useClass: FacebookPixelTrackingTesting,
          },
        ]
      : [],
    { provide: ErrorHandler, useClass: CustomErrorHandler },
    { provide: CAPTCHA_CONFIG, useValue: environment.captchaConfig },
    {
      provide: COOKIE_OPTIONS,
      deps: [PLATFORM_ID],
      useFactory: cookieOptionsFactory,
    },
    { provide: SITE_NAME, useValue: environment.siteName },
    // tslint:disable-next-line:deprecation FIXME: T106089
    { provide: NgModuleFactoryLoader, useClass: SystemJsNgModuleLoader },
    { provide: SITE_WIDE_LINK_TAGS, useValue: environment.linkTags },
    { provide: APPS_DOMAINS_MAP, useValue: environment.appsDomainsMap },
    { provide: ENVIRONMENT_NAME, useValue: environment.environmentName },
    { provide: APP_NAME, useValue: environment.appName },
    {
      provide: FREELANCER_HTTP_CONFIG,
      useValue: environment.freelancerHttpConfig,
    },
    { provide: FREELANCER_HTTP_AUTH_PROVIDERS, useExisting: Auth, multi: true },
    {
      provide: FREELANCER_HTTP_ABTEST_OVERRIDES_PROVIDER,
      useExisting: ABTestOverrides,
    },
    { provide: ABTEST_WHITELIST_COOKIE, useValue: environment.whitelistCookie },
  ],
  exports: [BrowserModule, TrackingModule, UiModule, ModalModule],
})
export class AppCommonModule {}
